"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Zap, TrendingUp, Users, Shield, Calendar,
  BarChart3, Palette, ArrowRight, Check, Star, Brain,
  Rocket, Target, Award, Clock, MessageSquare,
  Image as ImageIcon, Video, Edit3, Eye, ChevronRight, Pi,
  Wand2, Globe, Play, Heart, Code, Twitter, Github, Mail
} from "lucide-react";

/* ─── NAVIGATION ─── */
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-gray-950/80 backdrop-blur-2xl border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              <div className="relative w-10 h-10 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              DreamWorld
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {["Features", "AI Studio", "Community", "Team", "Pricing"].map((label) => (
              <a
                key={label}
                href={`#${label.toLowerCase().replace(" ", "-")}`}
                className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                {label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link to="/login">
              <button className="hidden sm:block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors">
                Sign In
              </button>
            </Link>
            <Link to="/login">
              <button className="px-5 py-2.5 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 transition-all">
                Get Started
              </button>
            </Link>
          </div>
        </div>
      </div>
    </motion.nav>
  );
}

/* ─── HERO ─── */
function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-[100px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-b from-purple-900/30 to-transparent blur-[80px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" />
            </span>
            <span className="text-sm font-semibold text-purple-300 tracking-wide uppercase">
              AI-Powered Creative Platform
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight mb-8 leading-[1.1]"
          >
            Reimagine Your
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
              Creative Vision
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto"
          >
            Generate stunning AI images, videos, and effects. Join a thriving
            community of creators and bring your imagination to life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/login">
              <button className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 text-white text-lg font-bold rounded-2xl shadow-xl shadow-purple-500/20 hover:shadow-purple-500/40 hover:-translate-y-1 transition-all">
                Start Creating Free
              </button>
            </Link>
            <a href="#features">
              <button className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white text-lg font-bold rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                Explore Features
              </button>
            </a>
          </motion.div>
        </div>

        {/* Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-600 to-pink-600 rounded-[2rem] opacity-20 blur-2xl" />
          <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-[1.5rem] shadow-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center gap-4 px-6 py-4 border-b border-white/5 bg-gray-900/50">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 text-center text-sm font-medium text-gray-500">
                dreamworld.ai/studio
              </div>
            </div>
            <div className="aspect-[16/9] bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900/50 relative p-6 sm:p-8">
              <div className="grid grid-cols-12 gap-4 h-full">
                <div className="col-span-3 bg-gray-800/50 rounded-xl border border-white/5 p-4 hidden sm:block">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg mb-6" />
                  <div className="space-y-3">
                    {[60, 80, 48, 64].map((w, i) => (
                      <div key={i} className="h-2 bg-white/5 rounded" style={{ width: `${w}%` }} />
                    ))}
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-9 space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    {["from-purple-500 to-pink-500", "from-blue-500 to-cyan-500", "from-green-500 to-emerald-500"].map((g, i) => (
                      <div key={i} className="bg-gray-800/50 rounded-xl border border-white/5 p-3">
                        <div className={`h-2 w-10 bg-gradient-to-r ${g} rounded mb-2`} />
                        <div className="h-5 w-16 bg-white/5 rounded" />
                      </div>
                    ))}
                  </div>
                  <div className="flex-1 bg-gray-800/50 rounded-xl border border-white/5 p-6 flex items-center justify-center min-h-[120px] sm:min-h-[200px]">
                    <div className="flex items-center gap-6 sm:gap-10">
                      {[
                        { label: "Prompt", color: "border-purple-500" },
                        { label: "AI Model", color: "border-blue-500" },
                        { label: "Result", color: "border-green-500" },
                      ].map((node, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <div className="h-0.5 w-6 sm:w-10 bg-gray-600" />}
                          <motion.div
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, delay: i * 0.5, repeat: Infinity }}
                            className={`px-4 py-3 bg-gray-900/80 rounded-lg border-2 ${node.color} shadow-lg`}
                          >
                            <span className="text-xs sm:text-sm font-bold text-gray-300">{node.label}</span>
                          </motion.div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── STATS BAR ─── */
function StatsBar() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const stats = [
    { icon: Users, value: "12.5K+", label: "Active Creators", color: "from-purple-500 to-pink-500" },
    { icon: Sparkles, value: "100K+", label: "Creations Made", color: "from-blue-500 to-cyan-500" },
    { icon: Clock, value: "99.9%", label: "Uptime", color: "from-green-500 to-emerald-500" },
    { icon: Star, value: "4.9/5", label: "User Rating", color: "from-yellow-500 to-orange-500" },
  ];

  return (
    <section ref={ref} className="py-12 lg:py-16 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="relative bg-gray-900/50 backdrop-blur-xl rounded-3xl border border-white/10 p-8 lg:p-12"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: i * 0.1, type: "spring" }}
                className="text-center"
              >
                <div className={`inline-flex w-14 h-14 items-center justify-center bg-gradient-to-br ${stat.color} rounded-2xl mb-4 shadow-xl`}>
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl lg:text-4xl font-black text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400 font-semibold">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── PLACEHOLDER SECTIONS (Phase 2 & 3 will fill these) ─── */
function FeaturesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const features = [
    { icon: ImageIcon, title: "AI Image Generation", desc: "Create stunning images from text prompts using cutting-edge AI models.", gradient: "from-purple-500 to-pink-500" },
    { icon: Video, title: "AI Video Creation", desc: "Generate dynamic videos and animations with AI-powered tools.", gradient: "from-blue-500 to-cyan-500" },
    { icon: Edit3, title: "Smart Image Editing", desc: "Edit and transform your photos with intelligent AI-assisted tools.", gradient: "from-green-500 to-emerald-500" },
    { icon: Wand2, title: "Custom Effects", desc: "Create and share your own AI effects with the community.", gradient: "from-yellow-500 to-orange-500" },
    { icon: Calendar, title: "Community Events", desc: "Participate in creative challenges and win exclusive prizes.", gradient: "from-indigo-500 to-purple-500" },
    { icon: Globe, title: "Creator Marketplace", desc: "Discover, follow, and collaborate with talented creators worldwide.", gradient: "from-pink-500 to-rose-500" },
  ];

  return (
    <section id="features" ref={ref} className="py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Rocket className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-bold text-purple-300">Powerful Features</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
            Everything You Need to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Create Magic</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">AI-powered tools for image generation, video creation, smart editing, and more.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              className="group bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-purple-500/30 hover:bg-gray-800/50 transition-all hover:-translate-y-1"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${f.gradient} rounded-xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                <f.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{f.title}</h3>
              <p className="text-gray-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── AI SHOWCASE ─── */
function AIShowcase() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const capabilities = [
    { icon: Sparkles, title: "Generate Captions & Prompts", desc: "AI writes the perfect prompts so you can focus on creating." },
    { icon: Clock, title: "Instant Generation", desc: "Get results in seconds with our optimized AI pipeline." },
    { icon: Target, title: "Style Transfer", desc: "Apply any artistic style to your creations with one click." },
    { icon: Award, title: "Quality Upscaling", desc: "Enhance resolution and details automatically with AI." },
  ];

  return (
    <section id="ai-studio" ref={ref} className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Visual */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} className="relative order-2 lg:order-1">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/10 p-6 lg:p-8 shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-3xl opacity-10" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Brain className="w-5 h-5 text-white" />
                  </motion.div>
                  <div>
                    <div className="font-bold text-white">AI Studio</div>
                    <div className="text-sm text-gray-500">Processing your creation...</div>
                  </div>
                </div>
                <div className="bg-purple-500/5 border border-purple-500/10 rounded-2xl p-4 mb-4">
                  <div className="text-xs text-gray-500 mb-2 font-semibold">Generated Prompt</div>
                  <div className="text-gray-300 text-sm leading-relaxed">"A majestic lion wearing a golden crown, standing in a neon-lit cyberpunk city at sunset, cinematic lighting, ultra detailed"</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3">
                    <Clock className="w-4 h-4 text-blue-400 mb-1" />
                    <div className="text-lg font-black text-white">3.2s</div>
                    <div className="text-xs text-gray-500 font-semibold">Gen Time</div>
                  </div>
                  <div className="bg-green-500/5 border border-green-500/10 rounded-xl p-3">
                    <Target className="w-4 h-4 text-green-400 mb-1" />
                    <div className="text-lg font-black text-white">1024px</div>
                    <div className="text-xs text-gray-500 font-semibold">Resolution</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-xs font-semibold text-gray-500">
                    <span>Generating...</span><span>92%</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: "0%" }} animate={{ width: "92%" }} transition={{ duration: 2, repeat: Infinity }} className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }} transition={{ duration: 5, repeat: Infinity }} className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-2xl">
              <Sparkles className="w-12 h-12 text-white" />
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-bold text-purple-300">Powered by Advanced AI</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-white mb-6 leading-tight">
              Next-Gen AI{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-pink-400">Creative Engine</span>
            </h2>
            <p className="text-lg text-gray-400 mb-10 leading-relaxed">
              We leverage the most advanced generative AI models to turn your ideas into stunning visuals — images, videos, and effects — in seconds.
            </p>
            <div className="space-y-4">
              {capabilities.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <Link to="/login">
              <button className="mt-8 px-7 py-3.5 bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 text-white font-bold rounded-2xl shadow-xl shadow-purple-500/20 hover:shadow-purple-500/40 hover:-translate-y-1 transition-all inline-flex items-center gap-2">
                Try AI Studio <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ─── */
function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const testimonials = [
    { name: "Sarah K.", role: "Digital Artist", content: "DreamWorld completely changed my creative workflow. The AI tools are incredibly powerful and the community is so inspiring!", rating: 5, avatar: "from-purple-500 to-pink-500" },
    { name: "Marcus T.", role: "Content Creator", content: "The video generation quality is insane. I've been able to create content that would've taken me days in just minutes.", rating: 5, avatar: "from-blue-500 to-cyan-500" },
    { name: "Aisha R.", role: "Photographer", content: "The image editing AI is a game-changer. It understands exactly what I want and the results are always stunning.", rating: 5, avatar: "from-green-500 to-emerald-500" },
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
            Loved by{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-pink-400">Thousands</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">See what our community of creators has to say.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.1 }} className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-purple-500/20 transition-all hover:-translate-y-1">
              <div className="flex gap-1 mb-4">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">"{t.content}"</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${t.avatar} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-white text-sm">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TEAM ─── */
function TeamSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const team = [
    { name: "Adama PRO", role: "Founder & CEO", bio: "Full-stack developer and entrepreneur with 5+ years building scalable software. Expertise in React, TypeScript, Node.js, and cloud architecture.", skills: ["React/TypeScript", "Node.js", "Cloud Architecture", "AI/ML"], gradient: "from-indigo-600 to-purple-600", twitter: "https://x.com/RichAdams0x" },
    { name: "Justin Lee", role: "Head of Marketing", bio: "Growth-focused marketer specializing in digital acquisition, brand positioning, and content strategy for SaaS products.", skills: ["Growth Marketing", "Brand Strategy", "Paid Ads", "Analytics"], gradient: "from-pink-600 to-purple-600", twitter: "https://x.com/CryptoLeeX" },
    { name: "Junman Itina", role: "Senior Developer", bio: "Full-stack engineer focused on performance, security, and scalable architectures. Experienced with high-traffic apps and real-time systems.", skills: ["TypeScript", "Node.js", "PostgreSQL", "DevOps"], gradient: "from-green-600 to-cyan-600", twitter: "https://x.com/itinajnr" },
  ];

  return (
    <section id="team" ref={ref} className="py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-bold text-purple-300">Meet the Team</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
            The People Behind{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">DreamWorld</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">A passionate team building the future of AI-powered creativity.</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {team.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2 + i * 0.1 }} className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-purple-500/20 transition-all hover:-translate-y-1 text-center">
              <div className={`w-20 h-20 mx-auto bg-gradient-to-br ${m.gradient} rounded-2xl flex items-center justify-center shadow-2xl mb-4`}>
                <span className="text-2xl font-black text-white">{m.name.split(" ").map(n => n[0]).join("")}</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-1">{m.name}</h3>
              <div className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-3">{m.role}</div>
              {m.twitter && (
                <a href={m.twitter} target="_blank" rel="noopener noreferrer" className="inline-flex w-8 h-8 items-center justify-center bg-white/5 rounded-lg hover:bg-white/10 transition-colors mb-4">
                  <Twitter className="w-4 h-4 text-gray-400" />
                </a>
              )}
              <p className="text-gray-400 text-sm leading-relaxed mb-4">{m.bio}</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {m.skills.map((s, j) => (
                  <span key={j} className="px-2 py-1 bg-purple-500/10 text-purple-300 text-xs font-semibold rounded-lg border border-purple-500/10">{s}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.5 }} className="bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/10 p-8 lg:p-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Code className="w-6 h-6 text-purple-400" />
            <h3 className="text-2xl font-black text-white">Our Technology Stack</h3>
          </div>
          <p className="text-gray-400 text-center mb-8 max-w-xl mx-auto">Built with modern, scalable technologies for reliability and performance.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "React", cat: "Frontend" }, { name: "TypeScript", cat: "Language" },
              { name: "Node.js", cat: "Backend" }, { name: "Firebase", cat: "Database" },
              { name: "Google Cloud", cat: "Infrastructure" }, { name: "Gemini AI", cat: "Generative AI" },
              { name: "Framer Motion", cat: "Animations" }, { name: "Tailwind CSS", cat: "Styling" },
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.6 + i * 0.05 }} className="px-4 py-2.5 bg-white/5 rounded-xl border border-white/5 hover:border-purple-500/20 transition-all">
                <div className="font-bold text-white text-sm">{t.name}</div>
                <div className="text-xs text-gray-500">{t.cat}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── COMMUNITY / PLATFORM ─── */
function CommunitySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const highlights = [
    { icon: Eye, title: "Discover Feed", desc: "Explore an endless stream of AI-generated art from creators worldwide.", gradient: "from-purple-500 to-pink-500" },
    { icon: Users, title: "Follow Creators", desc: "Build your network and get inspired by the best creative minds.", gradient: "from-blue-500 to-cyan-500" },
    { icon: TrendingUp, title: "Trending Content", desc: "See what's hot and climb the ranks to become a top creator.", gradient: "from-green-500 to-emerald-500" },
    { icon: Calendar, title: "Creative Events", desc: "Join community challenges, compete, and win exclusive prizes.", gradient: "from-yellow-500 to-orange-500" },
    { icon: Award, title: "Badges & Rewards", desc: "Earn recognition for your contributions and creative achievements.", gradient: "from-pink-500 to-rose-500" },
    { icon: Zap, title: "Flux Credits", desc: "Earn and spend credits to unlock premium AI features and content.", gradient: "from-indigo-500 to-purple-500" },
  ];

  return (
    <section id="community" ref={ref} className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-pink-600/10 rounded-full blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-pink-500/10 border border-pink-500/20 rounded-full mb-6">
            <Heart className="w-4 h-4 text-pink-400" />
            <span className="text-sm font-bold text-pink-300">Thriving Community</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
            Create, Connect,{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400">Get Inspired</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">DreamWorld is more than tools — it's a thriving ecosystem of artists and creators.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {highlights.map((h, i) => (
            <motion.div key={h.title} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.08 }} className="group bg-gray-900/50 backdrop-blur-xl rounded-2xl p-5 border border-white/5 hover:border-pink-500/20 transition-all hover:-translate-y-1">
              <div className={`w-11 h-11 bg-gradient-to-br ${h.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                <h.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1.5">{h.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{h.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── PRICING ─── */
function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const plans = [
    { name: "Free", price: "0", period: "forever", gradient: "from-gray-500 to-gray-600", icon: Zap, popular: false, features: ["50 Flux credits/month", "Basic AI image generation", "Standard resolution", "Community access", "5 gallery uploads"] },
    { name: "Creator", price: "5", period: "Pi/month", gradient: "from-purple-500 to-pink-500", icon: Sparkles, popular: true, features: ["500 Flux credits/month", "HD image & video gen", "Advanced AI editing", "Priority processing", "Unlimited uploads", "Custom effects"] },
    { name: "Studio", price: "15", period: "Pi/month", gradient: "from-indigo-500 to-blue-500", icon: Rocket, popular: false, features: ["2000 Flux credits/month", "4K ultra-HD generation", "All AI tools unlocked", "Commercial license", "API access", "Dedicated support"] },
  ];

  return (
    <section id="pricing" ref={ref} className="py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
            Simple,{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-pink-400">Transparent</span>{" "}
            Pricing
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">Start free. Upgrade when you need more power.</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              className={`relative bg-gray-900/50 backdrop-blur-xl rounded-2xl p-6 border-2 transition-all hover:-translate-y-1 ${
                plan.popular ? "border-purple-500/50 shadow-xl shadow-purple-500/10" : "border-white/5 hover:border-white/10"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                    <Star className="w-3 h-3 fill-current" /> Most Popular
                  </div>
                </div>
              )}
              <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${plan.gradient} mb-4`}>
                <plan.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              <div className="mt-3 mb-6">
                <span className="text-4xl font-black text-white">{plan.price}</span>
                <span className="text-gray-500 ml-1">{plan.period}</span>
              </div>
              <Link to="/login">
                <button className={`w-full py-3 rounded-xl font-bold text-sm mb-6 transition-all ${
                  plan.popular
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-purple-500/30"
                    : "bg-white/5 text-gray-300 hover:bg-white/10"
                }`}>
                  Get Started
                </button>
              </Link>
              <ul className="space-y-3">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── PI NETWORK ─── */
function PiNetworkSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-yellow-600/10 rounded-full blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="relative bg-gradient-to-br from-yellow-900/20 via-orange-900/20 to-amber-900/20 rounded-3xl border border-yellow-500/20 p-8 lg:p-12 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-bl-full" />
          <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-6">
                <Shield className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-bold text-yellow-300">Blockchain Powered</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-6 leading-tight">
                Secured by the{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">Pi Network</span>
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                DreamWorld is built on the Pi Network ecosystem. Sign in securely, purchase Flux credits with Pi, and enjoy a seamless Web3 creative experience.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Shield, title: "Secure Authentication", desc: "Sign in safely with your Pi Network account." },
                  { icon: Zap, title: "Pay with Pi", desc: "Purchase Flux credits using Pi cryptocurrency." },
                  { icon: Globe, title: "Decentralized", desc: "Your creations, your ownership. Built for Web3." },
                ].map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/5">
                    <div className="w-9 h-9 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                      <item.icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="w-56 h-56 lg:w-72 lg:h-72 bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-600 rounded-full flex items-center justify-center shadow-2xl shadow-orange-500/20"
              >
                <Pi className="w-24 h-24 lg:w-32 lg:h-32 text-white" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600" />
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ x: `${Math.random() * 100}%`, y: `${Math.random() * 100}%`, scale: 0 }}
            animate={{ scale: [0, 1, 0], opacity: [0, 0.6, 0] }}
            transition={{ duration: Math.random() * 3 + 2, repeat: Infinity, delay: Math.random() * 2 }}
            className="absolute w-1.5 h-1.5 bg-white rounded-full"
          />
        ))}
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            className="inline-flex w-20 h-20 items-center justify-center bg-white/20 backdrop-blur-xl rounded-3xl mb-8 shadow-2xl"
          >
            <Rocket className="w-10 h-10 text-white" />
          </motion.div>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            Ready to Create
            <br />Something Amazing?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join thousands of creators already using DreamWorld to bring their imagination to life with AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/login">
              <button className="px-10 py-5 bg-white text-gray-900 font-black text-lg rounded-2xl shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all inline-flex items-center gap-2">
                Get Started Free <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-white/90 font-medium text-sm">
            {[
              { icon: Check, text: "Free forever plan" },
              { icon: Shield, text: "No credit card required" },
              { icon: Star, text: "4.9/5 rated by creators" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  const links = {
    Product: [
      { label: "Features", href: "#features" },
      { label: "AI Studio", href: "#ai-studio" },
      { label: "Pricing", href: "#pricing" },
      { label: "Community", href: "#community" },
    ],
    Company: [
      { label: "About", href: "/about" },
      { label: "Team", href: "#team" },
      { label: "Blog", href: "#" },
      { label: "Contact", href: "#" },
    ],
    Legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/tos" },
    ],
  };

  return (
    <footer className="bg-gray-950 border-t border-white/5 py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-black text-white">DreamWorld</span>
            </div>
            <p className="text-gray-500 mb-6 leading-relaxed max-w-xs">
              The AI-powered creative platform where imagination meets technology. Create, share, and discover.
            </p>
            <div className="flex gap-3">
              {[Twitter, Github, Mail].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Icon className="w-4 h-4 text-gray-400" />
                </a>
              ))}
            </div>
          </div>
          {Object.entries(links).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">{category}</h3>
              <ul className="space-y-2.5">
                {items.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("/") ? (
                      <Link to={link.href} className="text-gray-500 hover:text-white transition-colors text-sm">
                        {link.label}
                      </Link>
                    ) : (
                      <a href={link.href} className="text-gray-500 hover:text-white transition-colors text-sm">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-600">© 2025 DreamWorld. All rights reserved.</p>
          <div className="flex gap-6 text-xs">
            <Link to="/privacy" className="text-gray-600 hover:text-white transition-colors">Privacy</Link>
            <Link to="/tos" className="text-gray-600 hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── MAIN EXPORT ─── */
export default function LandingPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 overflow-hidden antialiased">
      <Navigation />
      <HeroSection />
      <StatsBar />
      <FeaturesSection />
      <AIShowcase />
      <TestimonialsSection />
      <TeamSection />
      <CommunitySection />
      <PricingSection />
      <PiNetworkSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}
